import { CANCEL, CATEGORY, SELECT_CATEGORY_TYPE } from 'Pages/GMS/ISR/ISRConstants';
import React, {useEffect, useState} from 'react';
import CategoryListItem from './CategoryListItem';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Search from 'Components/DesignSystem/Search/Search';
import Button from 'Components/DesignSystem/Button/Button';
import {Data, TCategory, TIndexState, TProductCategory, TUpsertISRProjectDataModelsInput} from '../model';
import ConfirmModal from "../../../Components/RuleComponents/CommitConfirmModal";
import { toast } from 'Components/DesignSystem/Toast/useToast';

type TCategoryImportProps = {
    showCategoryMenu: boolean;
    setShowCategoryMenu: React.Dispatch<React.SetStateAction<boolean>>;
    categoryTypeList: string[];
    categoryData: Data[];
    selectedCategory: { type: string; data: TCategory[] } | null;
    setSelectedCategory: React.Dispatch<React.SetStateAction<{ type: string; data: TCategory[] } | null>>;
    setSelectedCategoriesMap: React.Dispatch<React.SetStateAction<{ [key: string]: TCategory[] }>>;
    selectedItems: TCategory[];
    setSelectedItems: React.Dispatch<React.SetStateAction<TCategory[]>>;
    setCategoryConfiguration: React.Dispatch<React.SetStateAction<TProductCategory | null>>;
    removeDataModelFieldMultiFilters: (deletedCategoryId?: string) => void;
    hasCategoryDependencies: (categoryId?: string) => boolean;
}

const CategoryImport = ({showCategoryMenu, setShowCategoryMenu, categoryTypeList, categoryData, selectedCategory, setSelectedCategory, setSelectedCategoriesMap, selectedItems, setSelectedItems, setCategoryConfiguration, removeDataModelFieldMultiFilters, hasCategoryDependencies}: TCategoryImportProps) => {
    const [searchInput, setSearchInput] = useState("");
    const [selectedCategoryType, setSelectedCategoryType] = useState<Data | null | undefined>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [selectedCategoryLocal, setSelectedCategoryLocal] = useState<TCategory | null | undefined>(null);

    useEffect(() => {
        const selectedCategoryArray = selectedCategory?.data ?? [];
        if(selectedCategoryArray.length > 0) {
            const storedCategory = selectedCategory?.data[0];
            let currentCategory: TCategory | null | undefined;
            // Resolve the category type by the STABLE category id (survives a
            // category / category-type rename), not by name. Matching by name
            // broke whenever a category was renamed in the Category module: the
            // stored (old) name no longer existed in the fresh list, so the type
            // never resolved and the category list rendered empty.
            const selectedCategoryItem = categoryData.filter((categoryType: any) => {
                const categories = categoryType.category.filter((category: any) =>
                    storedCategory?.id
                        ? category.id === storedCategory.id
                        : category.name === storedCategory?.name
                );
                if(categories.length > 0) {
                    currentCategory = categories[0];
                }
                return categories.length > 0;
            });
            if(selectedCategoryItem.length > 0) {
                setSelectedCategoryType(selectedCategoryItem[0]);
                // Only pre-select the radio when the stored name still matches the
                // current (possibly renamed) category name. If it was renamed the
                // list still shows the UPDATED name, but we leave it unselected so
                // the user must re-confirm it — that re-selection heals the stale
                // name stored on the model when they click Done.
                if(currentCategory && currentCategory.name === storedCategory?.name) {
                    setSelectedCategoryLocal(currentCategory);
                }
            }
        } else {
            const selectedCategoryItem = categoryData.filter((categoryType: any) => categoryType.name === selectedCategory?.type);
            if(selectedCategoryItem.length > 0) {
                setSelectedCategoryType(selectedCategoryItem[0]);
            }
        }
  }, []);

    const toggleMenu = () => {
        setShowCategoryMenu(!showCategoryMenu);
    };

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    // const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //     const selectedCategoryType = event.target.value;
    //     const selectedCategoryItem = categoryData.find((item: any) => item?.name === selectedCategoryType);
    //     setSelectAll(false);
    //     setSelectedCategory(selectedCategoryItem ? { type: selectedCategoryType, data: selectedCategoryItem.category } : null);
    //     setSelectedItems([]);
    //     setSelectedCategoriesMap((prevMap) => ({
    //       [selectedCategoryType]: [],
    //     }));
    //     clearCategories();
    // };

    const handleCategoryChange = (value: string | number) => {
        const selectedCategoryType = String(value);
        const selectedCategoryItem = categoryData.find((item: any) => item?.name === selectedCategoryType);
        setSelectedCategoryType(selectedCategoryItem);
      
        // Set the selected category and items
        setSelectedCategory(selectedCategoryItem ? { type: selectedCategoryType, data: selectedCategoryItem.category } : null);
      
        // Clear the selected items for the previous category type
        setSelectedItems([]);
      
        // Update the selected categories map for the current category type
        setSelectedCategoriesMap((prevMap) => ({
          [selectedCategoryType]: [],
        }));
        clearCategories();
      };
      

      const handleSelectCategoryLocal = (item: TCategory) => {
          setSelectedCategoryLocal(item);
      }


      const handleItemToggle = (item: TCategory) => {
        const isSelected = selectedItems.some((selectedItem) => selectedItem.id === item.id);
    
        // Clear previously selected items if not already selected
        const updatedSelectedItems = isSelected
            ? selectedItems.filter((selectedItem) => selectedItem.id !== item.id)
            : [item];
    
        setSelectedItems(updatedSelectedItems);
    
        if (selectedCategory && selectedCategory.type) {
            setSelectedCategoriesMap((prevMap) => ({
                ...prevMap,
                [selectedCategory.type]: updatedSelectedItems,
            }));
    
            setCategoryConfiguration((prevMap) => {
                if (!prevMap) return prevMap;
                const updatedData = {
                    ...prevMap.data,
                    categories: { type: selectedCategory.type, data: updatedSelectedItems },
                };
                return {
                    ...prevMap,
                    data: updatedData,
                };
            });
        }
    };
    

    const clearCategories = () => {
        setCategoryConfiguration((prevMap) => {
            if (!prevMap) return prevMap;
            const updatedData = {
                ...prevMap.data,
                categories: { type: '', data: [] },
            };
            return {
                ...prevMap,
                data: updatedData,
            };
        });
    }

    const handleDoneClick = () => {
        if(!selectedCategoryLocal) {
            toast.error('Select category to continue');
            return;
        }
        const isNewCategory = (selectedCategory?.data ?? []).length !== 1;
        if (!isNewCategory) {
            const prevCategoryId = selectedCategory?.data[0].id;
            if (prevCategoryId !== selectedCategoryLocal?.id) {
                removeDataModelFieldMultiFilters(prevCategoryId);
            }
        }
        handleItemToggle(selectedCategoryLocal);
        // if (selectedCategory && selectedCategory.type) {
        // setSelectedCategoriesMap((prevMap) => ({
        //     ...prevMap,
        //     [selectedCategory.type]: selectedItems,
        // }));
        // }

        // // Reset selected items and category after storing in the map
        // setSelectedItems([]);
        // setSelectedCategory(null);

        // // Close the menu or perform other actions as needed
        setShowCategoryMenu(false);
    };

    const filteredCategories = selectedCategoryType?.category.filter(category => {
            if (searchInput) {
                return category.name.toLowerCase().includes(searchInput.toLowerCase());
            }
            return true;
        }
    );

    return (
        <React.Fragment>
            {/*
                A DS SidePanel, not a hand-rolled drawer.
                It was a fixed full-screen overlay plus a `translate-x` panel, with a
                close control that was the literal character "X" in a bare button — no
                focus trap, no Escape, and nothing announcing it as a dialog.
            */}
            <SidePanel
                /*
                  Always open while mounted. `CategoryConfigurations` renders this only
                  behind `{showCategoryMenu && …}`, so the panel's own visibility flag
                  was always true in practice — and the legacy drawer stayed in the DOM
                  when "closed", leaving a full-screen `z-40` overlay over the page that
                  swallowed clicks. `onClose` still reports through `toggleMenu`.
                */
                isOpen
                onClose={toggleMenu}
                title={CATEGORY}
                tabs={[]}
                data-testid="category-menu"
                /* SidePanel puts its actions in the header — the slot the legacy
                   Cancel/Save pair occupied — so Cancel/Done move up out of the list.
                   They used to sit BELOW the category list, which meant scrolling a
                   long list to reach Done. */
                headerActions={(
                    <div id="category-actions" className='tw-flex tw-justify-end tw-gap-2'>
                        <Button
                            id="category-cancel-btn"
                            data-testid="components-categoryimport-button-4"
                            variant="secondary"
                            onClick={toggleMenu}
                        >
                            {CANCEL}
                        </Button>
                        <Button
                            id="category-done-btn"
                            data-testid="components-categoryimport-button-5"
                            variant="primary"
                            onClick={() => {
                                const isNewCategory = (selectedCategory?.data ?? []).length !== 1;
                                if(isNewCategory) {
                                    handleDoneClick();
                                } else {
                                    const prevCategoryId = selectedCategory?.data[0].id;
                                    // US 13052 / AC #3: re-selecting the SAME category
                                    // (same id — e.g. picking up its renamed name) never
                                    // warns; switching to a different category warns only
                                    // when filters/rules still reference the old one.
                                    if(prevCategoryId === selectedCategoryLocal?.id) {
                                        handleDoneClick();
                                    } else if(hasCategoryDependencies(prevCategoryId)) {
                                        setShowDialog(true);
                                    } else {
                                        handleDoneClick();
                                    }
                                }
                            }}
                        >
                            Done
                        </Button>
                    </div>
                )}
            >
                <div id="category-type-section">
                    <Dropdown
                        id="category-type-dropdown"
                        data-testid="components-categoryimport-select-2"
                        label=""
                        aria-label={SELECT_CATEGORY_TYPE}
                        placeholder={SELECT_CATEGORY_TYPE}
                        options={[
                            { value: SELECT_CATEGORY_TYPE, label: SELECT_CATEGORY_TYPE },
                            ...categoryTypeList.map((item) => ({ value: item, label: item })),
                        ]}
                        value={selectedCategoryType?.name ?? selectedCategory?.type ?? ''}
                        onChange={handleCategoryChange}
                        usePortal
                        className='tw-w-full'
                    />
                </div>
                <div id="category-search-section" className='tw-mt-2'>
                    {/* No `label`: there is no room for one in a 416px drawer, and
                        Search names itself from the placeholder — more than the raw
                        input had, which carried no accessible name at all. */}
                    <Search
                        id="category-search-input"
                        data-testid="components-categoryimport-input-3"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        placeholder="Search"
                    />
                </div>
                <div id="category-list-section" className='tw-mt-2'>
                    {filteredCategories && filteredCategories.length > 0 ? filteredCategories.map((item: TCategory, index: number) => {
                        return (
                            <CategoryListItem
                                id={`category-list-item-${index}`}
                                key={item.id}
                                item={item}
                                name="category-import-category"
                                isSelected={selectedCategoryLocal?.id === item.id}
                                onToggle={() => handleSelectCategoryLocal(item)}
                            />
                        );
                    }) : []}
                </div>
            </SidePanel>

            {showDialog ?
                <ConfirmModal
                    id="category-confirm-dialog"
                    title={'Switch category?'}
                    message={'Switching category will break existing filters and/or rules where this category is referenced. Are you sure you want to proceed?'}
                    confirmText={'Proceed'}
                    cancelText={'Cancel'}
                    onClose={() => {
                        setShowDialog(false);
                    }}
                    onConfirm={() => {
                        handleDoneClick();
                        setShowDialog(false);
                    }}
                />
            : null}
        </React.Fragment>
    );
}

export default CategoryImport;